import { Group, Stack, Tabs } from '@mantine/core';
import * as React from 'react';
import { useState } from 'react';
import { ExtractDataStep } from '@/types/automationSteps.ts';
import { HiMiniArrowTopRightOnSquare, HiOutlinePlus } from 'react-icons/hi2';
import AppIcon from '@/components/AppIcon.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import { ExtractSettings } from '@/pages/Editor/components/StepSidebar/StepForms/ExtractDataStepForm/ExtractFieldsModal/ExtractSettings.tsx';
import ExtractDataFieldsTable from '@/pages/Editor/components/StepSidebar/StepForms/ExtractDataStepForm/ExtractFieldsModal/DataFields/ExtractDataFieldsTable.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { getStepErrors, StepErrorType } from '@/types/stepErrorTypes.ts';
import ExtractInsightsFieldsTable from '@/pages/Editor/components/StepSidebar/StepForms/ExtractDataStepForm/ExtractFieldsModal/InsightFields/ExtractInsightsFieldsTable.tsx';
import InputSourceSelectField from '@/pages/Editor/components/SharedFields/InputSourceSelectField.tsx';
import { DataInput, InputSource } from '@/types/automationFields.ts';
import SingleDataInputSelectorInner from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleDataInputSelectorInner.tsx';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import {
  inputSourceAcceptsDataInput,
  inputSourceAcceptsUserSpecified,
} from '@/pages/Editor/components/SharedFields/utils.ts';

type props = {
  step: ExtractDataStep;
};

const ExtractFieldErrorTypes = [
  StepErrorType.ExtractFieldDuplicateId,
  StepErrorType.ExtractFieldInvalidId,
  StepErrorType.ExtractFieldEmptyName,
  StepErrorType.ExtractFieldInvalidDependency,
  StepErrorType.ExtractFieldDuplicateName,
  StepErrorType.ExtractFieldCircularDependency,
  StepErrorType.ExtractInsightInvalidId,
  StepErrorType.ExtractInsightEmptyName,
  StepErrorType.ExtractInsightEmptyDescription,
];

type Fields = {
  extract_fields_source: InputSource;
  extract_fields_data_input: DataInput;
};

const errorStyles = {
  borderColor: '#f34f52',
  background: '#fdeded',
};

const ExtractDataFields = ({ step }: props) => {
  const fieldsCount = step.fields.length + step.insights.length;

  const [modalOpened, setModalOpened] = useState(false);

  const fieldErrorsCount = getStepErrors(step, ExtractFieldErrorTypes).length;

  const [fieldsTab, setFieldsTab] = useState<'extract' | 'insights'>('extract');

  const { fields, onChangeNoDelay } = useUpdateStepFields<Fields>({ step });

  const invalidExtractFieldsDataInput = useStepSpecificError(
    step,
    StepErrorType.InvalidExtractFieldsDataInput,
  );

  return (
    <Stack w={'100%'} gap={'md'}>
      <InputSourceSelectField
        label="Fields Source"
        value={step.extract_fields_source}
        onChange={(value) => onChangeNoDelay({ extract_fields_source: value })}
        allowBoth={false}
      />
      {inputSourceAcceptsUserSpecified(fields.extract_fields_source) && (
        <>
          <Stack gap={0}>
            {fieldsCount === 0 ? (
              <StyledButton
                size={'md'}
                variant={'outline'}
                c="var(--velos-brand-blue)"
                leftSection={<AppIcon Icon={HiOutlinePlus} size={14} />}
                onClick={() => setModalOpened(true)}
              >
                Configure
              </StyledButton>
            ) : (
              <StyledButton
                size={'md'}
                variant={'outline'}
                c={fieldErrorsCount ? '#f34f52' : '#0f172a'}
                rightSection={<AppIcon Icon={HiMiniArrowTopRightOnSquare} size={14} />}
                onClick={() => setModalOpened(true)}
                style={fieldErrorsCount ? errorStyles : {}}
              >
                View fields ({fieldsCount})
              </StyledButton>
            )}
          </Stack>

          <StyledModal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            title="Field Information"
            size="90%"
            closeOnEscape={false}
            centered
            withCloseButton={true}
            styles={{ content: { height: '100%' } }}
          >
            <Stack gap="sm">
              <Tabs value={fieldsTab} onChange={(v) => setFieldsTab(v as 'extract' | 'insights')}>
                <Group w={'100%'} justify="space-between" align="center">
                  <Tabs.List w={'fit-content'}>
                    <Tabs.Tab value="extract" fw={fieldsTab === 'extract' ? 500 : 400}>
                      Extract Data
                    </Tabs.Tab>
                    <Tabs.Tab value="insights" fw={fieldsTab === 'insights' ? 500 : 400}>
                      Find Insights
                    </Tabs.Tab>
                  </Tabs.List>

                  <ExtractSettings step={step} type={fieldsTab} />
                </Group>

                <Tabs.Panel value="extract">
                  <ExtractDataFieldsTable step={step} />
                </Tabs.Panel>
                <Tabs.Panel value="insights">
                  <ExtractInsightsFieldsTable step={step} />
                </Tabs.Panel>
              </Tabs>
            </Stack>
          </StyledModal>
        </>
      )}

      {inputSourceAcceptsDataInput(fields.extract_fields_source) && (
        <SingleDataInputSelectorInner
          step={step}
          label={'Fields Data Input'}
          dataInputError={invalidExtractFieldsDataInput}
          dataInput={fields.extract_fields_data_input}
          onChange={(dataInput) => onChangeNoDelay({ extract_fields_data_input: dataInput })}
        />
      )}
    </Stack>
  );
};

export default ExtractDataFields;
